import { useCallback, useRef, useState } from 'react';

import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useEvaluateLoyaltyServiceOffers } from 'features/offer/hooks/use-evaluate-loyalty-service-offers';
import { usePaymentContext } from 'features/payment';
import { mapCardTypeToLoyalty } from 'features/payment/utils';
import { useEnableLoyaltyServiceRewards } from 'features/reward/hooks/use-enable-loyalty-service-rewards';
import { useEvaluateLoyaltyServiceRewards } from 'features/reward/hooks/use-evaluate-loyalty-service-rewards';
import { useStoreContext } from 'features/store';

import { CartPaymentCardType, LoyaltyPromotionType } from 'generated/graphql-gateway';
import { actions, useAppDispatch } from 'state/global-state';
import { useLoyaltyIncentivesEvaluation } from 'state/loyalty/hooks/use-loyalty-incentive-evaluation';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { getServiceModeFromLS } from 'state/service-mode';

import { ICartEntry } from '../types';
import {
  calculateCartSubTotal,
  getLoyaltyAppliedIncentives,
  hasOffers,
  hasRewards,
  isRewardCartEntry,
  mapCartEntriesToLoyaltyCartEntries,
} from '../utils';

/**
 * Specifically designed to be used for minimal cart entry evaluation
 * It will only evaluate the offers and rewards that are applied to the cart entries
 *
 * - In order to decouple this from the checkout in the future, we may need a different hook for this
 * - TODO: explore the possibility of using the useLoyaltyIncentivesEvaluation hook directly with a serverOrder
 * @returns
 */
export const useCartIncentivesEvaluation = ({ cartEntries }: { cartEntries: ICartEntry[] }) => {
  const { evaluateLoyaltyOffers, evaluateLoyaltyRewards } = useLoyaltyIncentivesEvaluation();
  const { evaluateAndApplyOffersFeedback } = useEvaluateLoyaltyServiceOffers();
  const { enabled: loyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();
  const {
    evaluateAndApplyRewardsFeedback,
    clearRewardsFeedback,
  } = useEvaluateLoyaltyServiceRewards();
  const { enabled: loyaltyServiceRewardsEnabled } = useEnableLoyaltyServiceRewards();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestIdRef = useRef(0);
  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const { checkoutPaymentMethod } = usePaymentContext();
  const paymentMethod = mapCardTypeToLoyalty(
    checkoutPaymentMethod?.credit?.cardType as CartPaymentCardType
  );
  const dispatch = useAppDispatch();

  const evaluateCartIncentives = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;

    if (!loyaltyId) {
      return;
    }
    const commonParams = {
      loyaltyId,
      serviceMode: getServiceModeFromLS(),
      subtotalAmount: calculateCartSubTotal(cartEntries),
      paymentMethod,
    };
    try {
      setIsLoading(true);
      const cartHasRewards = hasRewards(cartEntries);
      if (loyaltyServiceRewardsEnabled && !cartHasRewards) {
        clearRewardsFeedback();
      }

      // Only evaluate the offers and rewards that are applied to the entries that affects the subtotal
      const mainCartEntries = cartEntries.filter(entry => !isRewardCartEntry(entry));
      const offerEvaluation = (async () => {
        if (hasOffers(cartEntries)) {
          const appliedOfferIncentives = getLoyaltyAppliedIncentives({
            cartEntries,
            incentiveType: LoyaltyPromotionType.OFFER,
          });
          const offerCartEntries = mapCartEntriesToLoyaltyCartEntries(
            mainCartEntries,
            LoyaltyPromotionType.OFFER
          );

          // When the loyalty-service offers flag is on, check applied offers
          // against the loyalty service (its shared callback owns the stale-guard
          // and feedback dispatch). When it's off, keep the older check.
          if (loyaltyServiceOffersEnabled) {
            await evaluateAndApplyOffersFeedback({
              appliedIncentives: appliedOfferIncentives,
              cartEntries: offerCartEntries,
              serviceMode: commonParams.serviceMode,
              storeId,
            });
          } else {
            const result = await evaluateLoyaltyOffers({
              ...commonParams,
              appliedIncentives: appliedOfferIncentives,
              cartEntries: offerCartEntries,
            });

            if (result && requestId === latestRequestIdRef.current) {
              dispatch(actions.loyalty.setOffersFeedbackMap(result));
            }
          }
        }
      })();

      // Preserve the rewards-OFF sequencing exactly: offers settle before the
      // legacy reward evaluator starts.
      if (!loyaltyServiceRewardsEnabled) {
        await offerEvaluation;
      }

      let rewardEvaluation: Promise<void> = Promise.resolve();
      if (cartHasRewards) {
        const appliedRewardIncentives = getLoyaltyAppliedIncentives({
          cartEntries,
          incentiveType: LoyaltyPromotionType.REWARD,
        });
        const rewardCartEntries = mapCartEntriesToLoyaltyCartEntries(
          mainCartEntries,
          LoyaltyPromotionType.REWARD
        );

        // When the loyalty-service rewards flag is on, validate applied rewards
        // against the loyalty service (its shared callback owns the stale-guard
        // and feedback dispatch). When it's off, keep the legacy check.
        if (loyaltyServiceRewardsEnabled) {
          rewardEvaluation = evaluateAndApplyRewardsFeedback({
            appliedIncentives: appliedRewardIncentives,
            cartEntries: rewardCartEntries,
            serviceMode: commonParams.serviceMode,
            storeId,
          });
        } else {
          const result = await evaluateLoyaltyRewards({
            ...commonParams,
            appliedIncentives: appliedRewardIncentives,
            cartEntries: rewardCartEntries,
          });

          if (result && requestId === latestRequestIdRef.current) {
            dispatch(actions.loyalty.setRewardsFeedbackMap(result));
          }
        }
      }

      if (loyaltyServiceRewardsEnabled) {
        // Business-rule failures resolve into feedback maps. Query/runtime
        // failures still reject fast, while both independent requests have
        // already started from this cart snapshot.
        await Promise.all([offerEvaluation, rewardEvaluation]);
      }
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    loyaltyId,
    cartEntries,
    paymentMethod,
    loyaltyServiceOffersEnabled,
    loyaltyServiceRewardsEnabled,
    storeId,
    evaluateLoyaltyOffers,
    evaluateAndApplyOffersFeedback,
    dispatch,
    evaluateLoyaltyRewards,
    evaluateAndApplyRewardsFeedback,
    clearRewardsFeedback,
  ]);

  return { isLoading, evaluateCartIncentives } as const;
};
